import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchQuery, useRelayEnvironment } from 'react-relay';
import { homeFeedHouseholdQuery, homeFeedInspectionQuery } from './home-feed-queries';
import type { homeFeedQueriesHouseholdQuery as HouseholdQuery } from './__generated__/homeFeedQueriesHouseholdQuery.graphql';
import type { homeFeedQueriesInspectionQuery as InspectionQuery } from './__generated__/homeFeedQueriesInspectionQuery.graphql';
import {
    inspectionFailureKind,
    mapHousehold,
    mapInspection,
    type HomeFeedHouseholdOutcome,
    type HomeFeedInspectionOutcome,
} from '../screens/home-feed-view';
import type { InspectHomeFeed } from '../screens/home-feed-panel';

function householdFailureKind(error: unknown): 'not-found' | 'error' {
    return inspectionFailureKind(error) === 'not-found' ? 'not-found' : 'error';
}

/**
 * Binds the Home feed inspector's two reads to the panel. Both always go to the network: an inspection is
 * a dry run at the server's "now", so a cached answer would be wrong by definition, and a refresh must
 * come back with a new `evaluatedAt`. Failures are outcomes, not throws, so the panel can say which.
 */
export function useHomeFeed(
    vaultId: string,
    /**
     * Changes when something the household's Home feed state depends on changes (the vault's ring or its
     * HOME_FEED override) and has been confirmed; `null` while that write is in flight. A change refetches
     * the household, so a flag turned on from the same page stops reading as off.
     */
    refetchKey: string | null = null,
): {
    household: HomeFeedHouseholdOutcome;
    inspect: InspectHomeFeed;
    /** Refetches the household directory (flag state, members, Searches) without blanking the panel. */
    reloadHousehold: () => void;
    /** A reload failed while a household was already on screen; the old one is kept. */
    reloadFailed: boolean;
} {
    const environment = useRelayEnvironment();
    const [household, setHousehold] = useState<HomeFeedHouseholdOutcome>({ kind: 'loading' });
    const [reloads, setReloads] = useState(0);
    const [reloadFailed, setReloadFailed] = useState(false);
    const reloadHousehold = useCallback(() => setReloads((count) => count + 1), []);
    const seenKey = useRef(refetchKey);

    useEffect(() => {
        if (refetchKey === null || seenKey.current === refetchKey) return;
        seenKey.current = refetchKey;
        reloadHousehold();
    }, [refetchKey, reloadHousehold]);

    useEffect(() => {
        let current = true;
        // A reload keeps the household on screen (and the operator's picks); only a first load blanks it.
        setHousehold((previous) => (previous.kind === 'ok' ? previous : { kind: 'loading' }));
        setReloadFailed(false);
        fetchQuery<HouseholdQuery>(
            environment,
            homeFeedHouseholdQuery,
            { vaultId },
            { fetchPolicy: 'network-only' },
        )
            .toPromise()
            .then(
                (data) => {
                    if (current && data) {
                        setHousehold({ kind: 'ok', household: mapHousehold(data.homeFeedHousehold) });
                    }
                },
                (error: unknown) => {
                    if (!current) return;
                    // A failed reload must not blank a household the operator is already looking at.
                    setReloadFailed(true);
                    setHousehold((previous) =>
                        previous.kind === 'ok' ? previous : { kind: householdFailureKind(error) },
                    );
                },
            );
        return () => {
            current = false;
        };
    }, [environment, vaultId, reloads]);

    const inspect = useCallback<InspectHomeFeed>(
        async (memberAccountId, searchId): Promise<HomeFeedInspectionOutcome> => {
            try {
                const data = await fetchQuery<InspectionQuery>(
                    environment,
                    homeFeedInspectionQuery,
                    { vaultId, searchId, memberAccountId },
                    { fetchPolicy: 'network-only' },
                ).toPromise();
                return data
                    ? { kind: 'ok', inspection: mapInspection(data.homeFeedInspection) }
                    : { kind: 'error' };
            } catch (error) {
                return { kind: inspectionFailureKind(error) };
            }
        },
        [environment, vaultId],
    );

    return { household, inspect, reloadHousehold, reloadFailed };
}
